import React, { useState, useRef, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation
} from 'react-router-dom';

import Footer from './components/Footer';
import Home from './components/Home';
import ContactUs from './components/ContactUs';
import AboutUs from './components/AboutUs';
import ServicesISOStandards from './components/ServicesISOStandards';
import ISOStandards from './components/ISOStandards';
import OtherServices from './components/OtherServices';
import CertificationProcess from './components/CertificationProcess';
import Gallery from './components/Gallery';
import VisionMission from './components/VisionMission';
import CareerHome from './components/CareerHome';

import logo from './images/logo.jpg';

// Certification Pages
import ISO9001 from './cert/iso9001';
import ISO37001 from './cert/iso37001';
import ISO13485 from './cert/iso13485';
import ISO14001 from './cert/iso14001';
import ISO45001 from './cert/iso45001';
import ISO41001 from './cert/iso41001';
import ISO22301 from './cert/iso22301';
import ISO21001 from './cert/iso21001';
import ISO50001 from './cert/iso50001';
import ISO27001 from './cert/iso27001';
import IATF16949 from './cert/iatf16949';
import ISO22000 from './cert/iso22000';
import ISO20000 from './cert/iso20000';
import ISO42001 from './cert/iso42001';
import CMMI from './cert/cmmi';
import VAPT from './cert/vapt';
import SOC from './cert/soc';
import GDPR from './cert/gdpr';
import HIPAA from './cert/hipaa';
import HITRUST from './cert/hitrust';
import AI from './cert/ai';
import PCIDSS from './cert/pcidss';
import CE from './cert/ce';
import FCC from './cert/fcc';
import RoHS from './cert/rohs';
import ISO8124 from './cert/iso8124';
import Halal from './cert/halal';
import Kosher from './cert/kosher';
import LeadAuditor from './cert/lead-auditor';
import InternalAuditor from './cert/internal-auditor';


// ======================================================
// NAVBAR
// ======================================================

const Navbar = () => {
  const location = useLocation();

  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('lang') || 'en';
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // ======================================================
  // TRANSLATIONS
  // ======================================================

  const translations = {
    mr: {
      home: "मुख्य पृष्ठ",
      contact: "संपर्क साधा",
      about: "आमच्याबद्दल",
      services: "सेवा आणि मानके",
      process: "प्रमाणन प्रक्रिया",
      gallery: "गॅलरी",
      iso: "आयएसओ मानके",
      other: "इतर सेवा",
      career: "करिअर",
      menu: "मेनू",
      close: "बंद करा"
    },

    hi: {
      home: "मुख्य पृष्ठ",
      contact: "संपर्क करें",
      about: "हमारे बारे में",
      services: "सेवाएं और मानक",
      process: "प्रमाणीकरण प्रक्रिया",
      gallery: "गैलरी",
      iso: "आईएसओ मानक",
      other: "अन्य सेवाएँ",
      career: "करियर",
      menu: "मेनू",
      close: "बंद करें"
    },

    en: {
      home: "Home",
      contact: "Contact Us",
      about: "About Us",
      services: "Services & Standards",
      process: "Certification Process",
      gallery: "Gallery",
      iso: "ISO Standards",
      other: "Other Services",
      career: "Careers",
      menu: "Menu",
      close: "Close"
    }
  };

  const t = translations[language];

  // ======================================================
  // NAVIGATION ITEMS
  // ======================================================

  const navItems = [
    {
      path: "/",
      label: t.home
    },
    {
      path: "/about-us",
      label: t.about
    },
    {
      path: "/services-iso-standards",
      label: t.services,
      isDropdown: true
    },
    {
      path: "/certification-process",
      label: t.process
    },
    {
      path: "/gallery",
      label: t.gallery
    },
    {
      path: "/career",
      label: t.career
    },
    {
      path: "/contact-us",
      label: t.contact
    }
  ];

  const dropdownItems = [
    {
      path: "/iso-standards",
      label: t.iso
    },
    {
      path: "/other-services",
      label: t.other
    }
  ];

  // ======================================================
  // LANGUAGE CHANGE
  // ======================================================

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('lang', lang);
  };

  // ======================================================
  // CLOSE MENUS WHEN ROUTE CHANGES
  // ======================================================

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  // ======================================================
  // CLICK OUTSIDE
  // ======================================================

  useEffect(() => {
    const handleClickOutside = (event) => {

      // Close dropdown
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }

      // Close mobile menu
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ======================================================
  // ESC KEY
  // ======================================================

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
        setDropdownOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // ======================================================
  // MOBILE MENU TOGGLE
  // ======================================================

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
    setDropdownOpen(false);
  };

  // ======================================================
  // DROPDOWN TOGGLE
  // ======================================================

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setDropdownOpen((prev) => !prev);
  };

  // ======================================================
  // CLOSE MENU
  // ======================================================

  const closeMenus = () => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  };

  // ======================================================
  // RETURN
  // ======================================================

  return (
    <>
      <style>{`

        /* ==================================================
           GLOBAL
        ================================================== */

        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        body {
          background: linear-gradient(
            135deg,
            #f0f4f8,
            #e2e8f0,
            #cbd5e1
          );

          min-height: 100vh;

          font-family:
            'Segoe UI',
            Tahoma,
            Geneva,
            Verdana,
            sans-serif;
        }

        /* ==================================================
           APP CONTAINER
        ================================================== */

        .app-container {
          background: rgba(255, 255, 255, 0.95);

          box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.1);

          margin: 0 auto;

          min-height: 100vh;

          position: relative;
        }

        /* ==================================================
           NAVBAR
        ================================================== */

        .navbar {
          position: fixed;

          top: 0;
          left: 0;
          right: 0;

          height: 82px;

          background: #ffffff;

          display: flex;

          align-items: center;

          justify-content: space-between;

          padding: 0 2.5rem;

          box-shadow:
            0 4px 20px rgba(0, 0, 0, 0.12);

          z-index: 1000;
        }

        /* ==================================================
           LOGO
        ================================================== */

        .navbar-logo {
          display: flex;

          align-items: center;

          flex-shrink: 0;
        }

        .navbar-logo img {
          height: 62px;

          width: auto;

          border-radius: 12px;

          box-shadow:
            0 4px 12px rgba(0, 0, 0, 0.15);

          transition:
            transform 0.3s ease;
        }

        .navbar-logo img:hover {
          transform: scale(1.06);
        }

        /* ==================================================
           NAV LINKS
        ================================================== */

        .nav-links {
          display: flex;

          align-items: center;

          justify-content: center;

          gap: 0.5rem;

          position: relative;

          margin-left: auto;

          margin-right: 1rem;
        }

        .nav-link {
          display: flex;

          align-items: center;

          justify-content: center;

          text-decoration: none;

          color: #0A1D56;

          font-weight: 600;

          font-size: 0.92rem;

          padding: 0.55rem 1rem;

          border-radius: 30px;

          background: transparent;

          transition: all 0.3s ease;

          border: 1.6px solid transparent;

          white-space: nowrap;

          position: relative;

          cursor: pointer;
        }

        .nav-link:hover {
          background: #e3f2ff;

          color: #007bff;

          border-color: #007bff;

          transform: translateY(-1px);
        }

        .nav-link.active {
          background: #007bff;

          color: white;

          border-color: #007bff;

          font-weight: 700;

          box-shadow:
            0 3px 10px rgba(0, 123, 255, 0.3);
        }

        /* ==================================================
           DROPDOWN
        ================================================== */

        .dropdown-wrapper {
          position: relative;

          display: inline-block;
        }

        .dropdown {
          position: absolute;

          top: calc(100% + 8px);

          left: 50%;

          transform:
            translateX(-50%)
            translateY(-10px);

          background: white;

          min-width: 200px;

          border-radius: 12px;

          box-shadow:
            0 8px 25px rgba(0, 0, 0, 0.15);

          overflow: hidden;

          opacity: 0;

          visibility: hidden;

          transition:
            all 0.25s ease;

          z-index: 999;
        }

        .dropdown.open {
          opacity: 1;

          visibility: visible;

          transform:
            translateX(-50%)
            translateY(0);
        }

        .dropdown-item {
          display: block;

          padding: 0.85rem 1rem;

          color: #0A1D56;

          text-decoration: none;

          font-weight: 500;

          font-size: 0.9rem;

          transition:
            all 0.2s ease;

          text-align: center;
        }

        .dropdown-item:hover {
          background: #f0f7ff;

          color: #007bff;
        }

        .dropdown-item.active {
          background: #007bff;

          color: white;
        }

        /* ==================================================
           LANGUAGE SELECTOR
        ================================================== */

        .language-selector {
          flex-shrink: 0;

          display: flex;

          align-items: center;
        }

        .language-selector select {
          padding: 0.55rem 1rem;

          border-radius: 30px;

          border:
            1.8px solid #007bff;

          background: #f8f9ff;

          color: #007bff;

          font-weight: 600;

          font-size: 0.9rem;

          cursor: pointer;

          transition:
            all 0.3s ease;

          outline: none;
        }

        .language-selector select:hover {
          background: #e3f2ff;

          transform: scale(1.02);
        }

        /* ==================================================
           MOBILE MENU BUTTON
        ================================================== */

        .mobile-menu-button {
          display: none;

          width: 45px;

          height: 45px;

          border: none;

          border-radius: 10px;

          background: #007bff;

          color: white;

          font-size: 1.6rem;

          cursor: pointer;

          align-items: center;

          justify-content: center;

          transition: all 0.3s ease;
        }

        .mobile-menu-button:hover {
          background: #0056b3;

          transform: scale(1.04);
        }

        /* ==================================================
           PAGE CONTENT
        ================================================== */

        .page-content {
          margin-top: 82px;

          padding: 2rem;

          min-height:
            calc(100vh - 150px);
        }

        /* ==================================================
           FOOTER
        ================================================== */

        footer {
          text-align: center;

          padding: 22px;

          font-size: 14px;

          color: #4a5568;

          background: #f7fafc;

          border-top:
            1px solid #e2e8f0;
        }

        /* ==================================================
           TABLET
        ================================================== */

        @media (max-width: 1200px) {

          .navbar {
            padding: 0 1.5rem;
          }

          .nav-links {
            gap: 0.25rem;
          }

          .nav-link {
            font-size: 0.84rem;

            padding:
              0.5rem 0.7rem;
          }

          .language-selector select {
            font-size: 0.82rem;

            padding:
              0.5rem 0.8rem;
          }
        }

        /* ==================================================
           MOBILE / TABLET MENU
        ================================================== */

        @media (max-width: 992px) {

          .navbar {
            height: 72px;

            padding:
              0 1rem;
          }

          .navbar-logo img {
            height: 52px;
          }

          /* Hide desktop links */

          .nav-links {
            position: absolute;

            top: 72px;

            left: 0;

            right: 0;

            width: 100%;

            background: white;

            display: flex;

            flex-direction: column;

            align-items: stretch;

            justify-content: flex-start;

            gap: 0;

            margin: 0;

            padding: 1rem;

            box-shadow:
              0 10px 25px rgba(0,0,0,0.15);

            max-height: 0;

            overflow: hidden;

            opacity: 0;

            visibility: hidden;

            transition:
              max-height 0.35s ease,
              opacity 0.25s ease,
              visibility 0.25s ease;

            z-index: 998;
          }

          /* Open mobile menu */

          .nav-links.mobile-open {
            max-height: calc(100vh - 72px);

            overflow-y: auto;

            opacity: 1;

            visibility: visible;
          }

          .nav-link {
            width: 100%;

            justify-content: flex-start;

            padding:
              0.9rem 1rem;

            margin-bottom: 0.35rem;

            border-radius: 10px;

            font-size: 1rem;
          }

          .nav-link:hover {
            transform: none;
          }

          /* Dropdown wrapper */

          .dropdown-wrapper {
            width: 100%;
          }

          /* Mobile dropdown */

          .dropdown {
            position: static;

            width: 100%;

            min-width: 0;

            transform: none;

            opacity: 0;

            visibility: hidden;

            max-height: 0;

            box-shadow: none;

            border-radius: 8px;

            background: #f5f9ff;

            margin:
              0 0 0.5rem 0;

            overflow: hidden;

            transition:
              max-height 0.3s ease,
              opacity 0.25s ease,
              visibility 0.25s ease;
          }

          .dropdown.open {
            transform: none;

            opacity: 1;

            visibility: visible;

            max-height: 200px;
          }

          .dropdown-item {
            text-align: left;

            padding:
              0.8rem 1.5rem;

            border-bottom:
              1px solid #e5edf7;

            font-size: 0.95rem;
          }

          .dropdown-item:last-child {
            border-bottom: none;
          }

          /* Mobile language selector */

          .language-selector {
            display: none;

            position: absolute;

            top: 72px;

            left: 0;

            right: 0;

            background: white;

            padding:
              0.75rem 1rem;

            border-top:
              1px solid #eee;

            z-index: 999;
          }

          /* Hamburger */

          .mobile-menu-button {
            display: flex;
          }

          /* Page */

          .page-content {
            margin-top: 72px;

            padding:
              1.2rem 0.8rem;
          }
        }

        /* ==================================================
           SMALL MOBILE
        ================================================== */

        @media (max-width: 576px) {

          .navbar {
            height: 68px;

            padding:
              0 0.8rem;
          }

          .navbar-logo img {
            height: 48px;

            border-radius: 9px;
          }

          .mobile-menu-button {
            width: 42px;

            height: 42px;

            font-size: 1.45rem;

            border-radius: 9px;
          }

          .nav-links {
            top: 68px;

            padding:
              0.75rem;
          }

          .nav-links.mobile-open {
            max-height:
              calc(100vh - 68px);
          }

          .nav-link {
            font-size: 0.95rem;

            padding:
              0.85rem 0.9rem;
          }

          .dropdown-item {
            font-size: 0.9rem;

            padding:
              0.75rem 1.3rem;
          }

          .page-content {
            margin-top: 68px;

            padding:
              1rem 0.6rem;
          }
        }

        /* ==================================================
           VERY SMALL MOBILE
        ================================================== */

        @media (max-width: 380px) {

          .navbar-logo img {
            height: 44px;
          }

          .mobile-menu-button {
            width: 40px;

            height: 40px;
          }

          .nav-link {
            font-size: 0.9rem;
          }
        }

      `}</style>


      {/* ==================================================
          NAVBAR
      ================================================== */}

      <nav className="navbar" ref={mobileMenuRef}>

        {/* LOGO */}

        <Link
          to="/"
          className="navbar-logo"
          onClick={closeMenus}
        >
          <img
            src={logo}
            alt="CertNexuse"
          />
        </Link>


        {/* ==================================================
            DESKTOP / MOBILE NAV LINKS
        ================================================== */}

        <div
          className={`nav-links ${
            mobileMenuOpen ? 'mobile-open' : ''
          }`}
          ref={dropdownRef}
        >

          {navItems.map((item) => (

            item.isDropdown ? (

              <div
                key={item.path}
                className="dropdown-wrapper"

                onMouseEnter={() => {
                  if (window.innerWidth > 992) {
                    setDropdownOpen(true);
                  }
                }}

                onMouseLeave={() => {
                  if (window.innerWidth > 992) {
                    setDropdownOpen(false);
                  }
                }}
              >

                {/* Dropdown Main Button */}

                <div
                  className={`nav-link ${
                    location.pathname.startsWith('/iso-standards') ||
                    location.pathname.startsWith('/other-services')
                      ? 'active'
                      : ''
                  }`}
                  onClick={toggleDropdown}
                  style={{
                    cursor: 'pointer'
                  }}
                >

                  {item.label}

                  <span
                    style={{
                      fontSize: '0.75rem',
                      marginLeft: '7px',
                      transition: 'transform 0.3s ease',
                      display: 'inline-block',
                      transform:
                        dropdownOpen
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)'
                    }}
                  >
                    ▼
                  </span>

                </div>


                {/* DROPDOWN */}

                <div
                  className={`dropdown ${
                    dropdownOpen ? 'open' : ''
                  }`}
                >

                  {dropdownItems.map((sub) => (

                    <Link
                      key={sub.path}
                      to={sub.path}

                      className={`dropdown-item ${
                        location.pathname === sub.path
                          ? 'active'
                          : ''
                      }`}

                      onClick={closeMenus}
                    >
                      {sub.label}
                    </Link>

                  ))}

                </div>

              </div>

            ) : (

              <Link
                key={item.path}
                to={item.path}

                className={`nav-link ${
                  location.pathname === item.path
                    ? 'active'
                    : ''
                }`}

                onClick={closeMenus}
              >
                {item.label}
              </Link>

            )

          ))}


          {/* ==================================================
              MOBILE LANGUAGE SELECTOR
          ================================================== */}

          <div
            className="mobile-language-selector"
            style={{
              display: 'none'
            }}
          >
          </div>

        </div>


        {/* ==================================================
            DESKTOP LANGUAGE SELECTOR
        ================================================== */}

        <div className="language-selector">

          <select
            value={language}
            onChange={(e) =>
              changeLanguage(e.target.value)
            }
          >

            <option value="mr">
              मराठी
            </option>

            <option value="hi">
              हिन्दी
            </option>

            <option value="en">
              English
            </option>

          </select>

        </div>


        {/* ==================================================
            MOBILE HAMBURGER
        ================================================== */}

        <button
          className="mobile-menu-button"

          onClick={toggleMobileMenu}

          aria-label={
            mobileMenuOpen
              ? t.close
              : t.menu
          }

          aria-expanded={mobileMenuOpen}
        >

          {mobileMenuOpen ? '✕' : '☰'}

        </button>

      </nav>


      {/* ==================================================
          MOBILE LANGUAGE BAR
      ================================================== */}

      {mobileMenuOpen && (
        <div
          className="mobile-language-bar"
          style={{
            position: 'fixed',
            top: '72px',
            left: 0,
            right: 0,
            zIndex: 997,
            background: '#ffffff',
            padding: '0.8rem 1rem',
            borderTop: '1px solid #eeeeee',
            borderBottom: '1px solid #eeeeee',
            boxShadow: '0 5px 15px rgba(0,0,0,0.08)'
          }}
        >

          <select
            value={language}
            onChange={(e) =>
              changeLanguage(e.target.value)
            }

            style={{
              width: '100%',
              padding: '0.7rem 1rem',
              borderRadius: '10px',
              border: '1.5px solid #007bff',
              background: '#f8f9ff',
              color: '#007bff',
              fontWeight: '600',
              fontSize: '0.95rem',
              outline: 'none'
            }}
          >

            <option value="mr">
              मराठी
            </option>

            <option value="hi">
              हिन्दी
            </option>

            <option value="en">
              English
            </option>

          </select>

        </div>
      )}

    </>
  );
};


// ======================================================
// MAIN APP
// ======================================================

const App = () => {

  return (

    <Router>

      <div className="app-container">

        {/* NAVBAR */}

        <Navbar />


        {/* PAGE CONTENT */}

        <div className="page-content">

          <Routes>

            {/* MAIN PAGES */}

            <Route
              path="/"
              element={<Home />}
            />

            <Route
              path="/about-us"
              element={<AboutUs />}
            />

            <Route
              path="/services-iso-standards"
              element={<ServicesISOStandards />}
            />

            <Route
              path="/certification-process"
              element={<CertificationProcess />}
            />

            <Route
              path="/gallery"
              element={<Gallery />}
            />

            <Route
              path="/contact-us"
              element={<ContactUs />}
            />

            <Route
              path="/iso-standards"
              element={<ISOStandards />}
            />

            <Route
              path="/other-services"
              element={<OtherServices />}
            />

            <Route
              path="/vision-mission"
              element={<VisionMission />}
            />

            {/* CAREER */}

            <Route
              path="/career"
              element={<CareerHome />}
            />


            {/* ==================================================
                CERTIFICATION ROUTES
            ================================================== */}

            <Route
              path="/cert/iso9001"
              element={<ISO9001 />}
            />

            <Route
              path="/cert/iso37001"
              element={<ISO37001 />}
            />

            <Route
              path="/cert/iso13485"
              element={<ISO13485 />}
            />

            <Route
              path="/cert/iso14001"
              element={<ISO14001 />}
            />

            <Route
              path="/cert/iso45001"
              element={<ISO45001 />}
            />

            <Route
              path="/cert/iso41001"
              element={<ISO41001 />}
            />

            <Route
              path="/cert/iso22301"
              element={<ISO22301 />}
            />

            <Route
              path="/cert/iso21001"
              element={<ISO21001 />}
            />

            <Route
              path="/cert/iso50001"
              element={<ISO50001 />}
            />

            <Route
              path="/cert/iso27001"
              element={<ISO27001 />}
            />

            <Route
              path="/cert/iatf16949"
              element={<IATF16949 />}
            />

            <Route
              path="/cert/iso22000"
              element={<ISO22000 />}
            />

            <Route
              path="/cert/iso20000"
              element={<ISO20000 />}
            />

            <Route
              path="/cert/iso42001"
              element={<ISO42001 />}
            />

            <Route
              path="/cert/cmmi"
              element={<CMMI />}
            />

            <Route
              path="/cert/vapt"
              element={<VAPT />}
            />

            <Route
              path="/cert/soc"
              element={<SOC />}
            />

            <Route
              path="/cert/gdpr"
              element={<GDPR />}
            />

            <Route
              path="/cert/hipaa"
              element={<HIPAA />}
            />

            <Route
              path="/cert/hitrust"
              element={<HITRUST />}
            />

            <Route
              path="/cert/ai"
              element={<AI />}
            />

            <Route
              path="/cert/pcidss"
              element={<PCIDSS />}
            />

            <Route
              path="/cert/ce"
              element={<CE />}
            />

            <Route
              path="/cert/fcc"
              element={<FCC />}
            />

            <Route
              path="/cert/rohs"
              element={<RoHS />}
            />

            <Route
              path="/cert/iso8124"
              element={<ISO8124 />}
            />

            <Route
              path="/cert/halal"
              element={<Halal />}
            />

            <Route
              path="/cert/kosher"
              element={<Kosher />}
            />

            <Route
              path="/cert/lead-auditor"
              element={<LeadAuditor />}
            />

            <Route
              path="/cert/internal-auditor"
              element={<InternalAuditor />}
            />

          </Routes>

        </div>


        {/* FOOTER */}

        {/*
        <Footer />
        */}

      </div>

    </Router>
  );
};

export default App;
